import * as authService from './authService';
import * as favoritesService from './favoritesService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const reviewsDataCache = new Map(); // Key: `${cityName}_${locationId}`, Value: { reviews: Review[], timestamp: number }
const usernamesDataCache = new Map(); // Key: userId, Value: { username: string, timestamp: number }
const favoritesDataCache = new Map(); // Added: Key: 'currentUserFavorites', Value: { favorites: Favorite[], timestamp: number }

const CACHE_DURATION_MS = {
    reviews: 5 * 60 * 1000,    // 5 minutes for reviews
    usernames: 30 * 60 * 1000, // 30 minutes for usernames (more static)
    favorites: 5 * 60 * 1000,  // Added: 5 minutes for favorites
};

async function fetchReviewsFromAPI(cityName, locationId) {
    try {
        const response = await fetch(`${API_BASE_URL}/reviews/${encodeURIComponent(cityName)}/${encodeURIComponent(locationId)}/reviews`);
        if (!response.ok) {
            // console.error(`API Error: Failed to fetch reviews for ${cityName}/${locationId} - ${response.status}`);
            throw new Error(`Failed to fetch reviews: ${response.status}`);
        }
        const reviews = await response.json();
        return reviews.map(r => ({ ...r, timestamp: new Date(r.timestamp) })); // Ensure timestamp is a Date object
    } catch (error) {
        // console.error(`Network/Fetch Error: Fetching reviews for ${cityName}/${locationId}`, error);
        throw error;
    }
}

async function fetchUsernameFromAPI(userId) {
    // authService returns an object: { displayUsername, error }
    const { displayUsername, error } = await authService.getUserDisplayName(userId);
    if (error) {
        // console.error(`Failed to fetch username for ${userId}:`, error);
        return 'Anonymous'; // Consistent fallback name
    }
    return displayUsername;
}

export async function getDisplayUsername(userId) { // Token removed
    const cachedUsernameEntry = usernamesDataCache.get(userId);
    if (cachedUsernameEntry && (Date.now() - cachedUsernameEntry.timestamp < CACHE_DURATION_MS.usernames)) {
        return cachedUsernameEntry.username;
    }

    try {
        const username = await fetchUsernameFromAPI(userId); // Token removed
        usernamesDataCache.set(userId, { username: username, timestamp: Date.now() });
        return username;
    } catch (error) {
        // console.error(`Error in getDisplayUsername for ${userId}:`, error);
        return 'Anonymous'; // Consistent fallback name
    }
}

export async function getReviewsWithUsernames(cityName, locationId, token) { // Token is kept for potential future use but not for usernames
    const reviewCacheKey = `${cityName}_${locationId}`;
    const cachedReviewsEntry = reviewsDataCache.get(reviewCacheKey);

    let rawReviews;
    if (cachedReviewsEntry && (Date.now() - cachedReviewsEntry.timestamp < CACHE_DURATION_MS.reviews)) {
        rawReviews = cachedReviewsEntry.reviews;
    } else {
        try {
            rawReviews = await fetchReviewsFromAPI(cityName, locationId);
            reviewsDataCache.set(reviewCacheKey, { reviews: rawReviews, timestamp: Date.now() });
        } catch (error) {
            // console.error(`Error fetching and caching reviews for ${reviewCacheKey}:`, error);
            return []; // Return empty or throw, depending on desired error handling
        }
    }

    if (!rawReviews || rawReviews.length === 0) {
        return [];
    }

    // Fetch usernames for all unique userIds in the current list of reviews
    const reviewsWithUsernames = await Promise.all(
        rawReviews.map(async (review) => {
            // Call the corrected, public, caching function
            const displayName = await getDisplayUsername(review.userId); // No token passed
            return { ...review, displayUsername: displayName };
        })
    );
    return reviewsWithUsernames;
}

export function invalidateLocationReviewsCache(cityName, locationId) {
    const reviewCacheKey = `${cityName}_${locationId}`;
    reviewsDataCache.delete(reviewCacheKey);
    // console.log(`Cache invalidated for reviews: ${reviewCacheKey}`);
}

// --- Favorites Cache Functions ---

export function invalidateUserFavoritesCache() {
    favoritesDataCache.delete('currentUserFavorites');
    // console.log('User favorites cache invalidated.');
}

export async function getCachedUserFavorites(token) {
    if (!token) return [];

    const cachedEntry = favoritesDataCache.get('currentUserFavorites');
    if (cachedEntry && (Date.now() - cachedEntry.timestamp < CACHE_DURATION_MS.favorites)) {
        return cachedEntry.favorites;
    }

    try {
        const fetchedFavorites = await favoritesService.getUserFavorites(token);
        favoritesDataCache.set('currentUserFavorites', { favorites: fetchedFavorites, timestamp: Date.now() });
        return fetchedFavorites;
    } catch (error) {
        console.error('Error fetching and caching user favorites:', error);
        return []; 
    }
}

export async function addFavoriteToCache(token, cityName, locationId, locationName) {
    if (!token) return { success: false, message: 'Authentication token required.' };
    try {
        const result = await favoritesService.addToFavorites(token, cityName, locationId, locationName);
        if (result.success) {
            invalidateUserFavoritesCache(); // Invalidate cache on successful addition
        }
        return result; // Return the original result from the service call
    } catch (error) {
        console.error('Error in addFavoriteToCache:', error);
        return { success: false, message: error.message || 'Failed to add favorite in cache service' };
    }
}

export async function removeFavoriteFromCache(token, cityName, locationId) {
    if (!token) return { success: false, message: 'Authentication token required.' };
    try {
        const result = await favoritesService.removeFromFavorites(token, cityName, locationId);
        if (result.success) {
            invalidateUserFavoritesCache(); // Invalidate cache on successful removal
        }
        return result; // Return the original result from the service call
    } catch (error) {
        console.error('Error in removeFavoriteFromCache:', error);
        return { success: false, message: error.message || 'Failed to remove favorite in cache service' };
    }
}

// No explicit invalidation for usernamesCache here, relies on its own longer TTL.
// If a username changes, it would take up to CACHE_DURATION_MS.usernames to update everywhere. 